import React from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import Alert from 'Components/DesignSystem/Alert/Alert';

export interface RuleChangeConfirmDialogProps {
  /** Rendered only while true; the caller owns the pending change. */
  isOpen: boolean;
  /** Dialog title — "Change Rule Type?" / "Change Category?". */
  title: string;
  /** What the change will cost. */
  message: string;
  /** Label on the destructive-confirm button. Bootbox's default was "Confirm". */
  confirmLabel?: string;
  onConfirm: () => void;
  /** Cancel, the X, Escape and a backdrop click all resolve here. */
  onCancel: () => void;
  'data-testid'?: string;
}

/**
 * The "this will delete your rule logic — continue?" confirm, on the DS `Dialog`
 * (§ 113).
 *
 * It replaces `Bootbox.confirm` at **two call sites only** — `RuleHeader`'s
 * "Change Rule Type?" and `RuleCategorySelector`'s "Change Category?".
 * `Common/BootBoxManager` itself is untouched and still serves its other eleven
 * consumers, two of which (`CBMCategorySelector`, `MatrixComponent`) are on this very
 * page but are out of scope. So Bootbox is still reachable from `/gms/rule/edit/:id`
 * via the CBM matrix; that is deliberate, not a missed call site.
 *
 * **What Bootbox actually was.** `window["bootbox"]` — a global jQuery/Bootstrap
 * plugin, fed an **HTML string** for its body:
 *
 * ```
 * message: `<div class="text-center" style="font-family: Noto Sans">
 *             <strong>${message}</strong>
 *           </div>`
 * ```
 *
 * with `className: "button-outlined …"` / `"button-fill …"` on its buttons — the same
 * two global classes from `public/css/main.css`, outside the build, that § 112 removed
 * from the rest of this screen. It could not be brought onto the design system by
 * restyling; only by replacing the call.
 *
 * Note the interpolation: `message` is dropped into an HTML string unescaped. Both
 * in-scope call sites pass the same hardcoded constant (`CHANGE_RULE_WARNING`), so
 * nothing was ever injectable — but a caller passing user text would have been. This
 * component takes `message` as a React child, where escaping is not optional.
 *
 * **Behaviour preserved exactly.** Bootbox ran its callback only on Confirm
 * (`callback: (result) => { if (result) … }`); Cancel and dismissal did nothing at
 * all. Same here: `onConfirm` fires only from the confirm button, and the caller drops
 * its pending change on `onCancel`. The two titles are in `bootBoxTitles`
 * (`Common/BootBoxConstants`), which is what suppressed Bootbox's extra "Do you really
 * want to delete?" / "This cannot be undone" copy — so the body is just the message,
 * exactly as before.
 *
 * **Three things the shared component brings that the string could not:** a
 * `role="dialog"` with an accessible name, focus moved in on open and returned to the
 * trigger on close, and Escape / backdrop dismissal. Bootbox was given
 * `closeButton: false`, so the legacy dialog had **no** dismiss affordance beyond its
 * Cancel button; `Dialog` always renders an X, and every dismissal route resolves to
 * Cancel — which is the safe direction for a destructive confirm.
 *
 * The warning rides an `Alert tone="warning"` rather than `Dialog`'s own `tone` glyph,
 * so this matches the Unsaved Changes confirm sitting one interaction away on the same
 * screen. Passing both would render the triangle twice.
 */
const RuleChangeConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  onConfirm,
  onCancel,
  'data-testid': dataTestId = 'rule-change-confirm',
}: RuleChangeConfirmDialogProps) => {
  if (!isOpen) return null;

  return (
    <Dialog
      isOpen
      /* `md`, not `sm` — the § 113 measurement. A footer's buttons are laid out at
         their natural widths and `sm` (384px, minus the footer's 48px of inset) leaves
         336px, which two buttons clear easily but three do not. Fixed at `md` here so
         both confirms on this screen are the same width. */
      size="md"
      title={title}
      onClose={onCancel}
      data-testid={dataTestId}
      footer={
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
          <Button
            variant="secondary"
            className="tw-whitespace-nowrap"
            data-testid={`${dataTestId}-cancel-btn`}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="tw-whitespace-nowrap"
            data-testid={`${dataTestId}-confirm-btn`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <Alert tone="warning" data-testid={`${dataTestId}-note`}>
        {message}
      </Alert>
    </Dialog>
  );
};

export default RuleChangeConfirmDialog;
